import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import Select from "react-select";
import { debounce } from "lodash";
import "./AssignmentMaster.css";

export default function AssignmentDetails() {
  const [state, setState] = useState({
    mcourses: [],
    courseSubDetails: [],
    assignments: [],
    loading: true,
    formSubmitting: false,
    formData: {
      assignmentId: 0,
      courseId: 0,
      courseSubDetailId: 0,
      courseName: "",
      assignmentTittle: "",
      assignmentDescription: "",
      assignmentDueDate: "",
      assignmentTotalPoints: 0,
      assignmentPassingMarks: 0,
      assignmentDetailStatus: true,
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5,
  });

  const updateState = (newState) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  useEffect(() => {
    fetchAssignments();
    fetchMainCourses();
    fetchCourseSubDetails();
  }, []);

  const fetchMainCourses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Course/SuperAdminCourseMasterList`, {
        headers: getHeaders(),
      });
      updateState({
        mcourses: Array.isArray(response.data) ? response.data : [],
      });
    } catch (error) {
      console.error("Error fetching main courses:", error);
      Swal.fire("Error!", "Failed to load courses.", "error");
    }
  };

  const fetchCourseSubDetails = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Course/CourseSubDetailsList`, {
        headers: getHeaders(),
      });
      updateState({
        courseSubDetails: Array.isArray(response.data) ? response.data : [],
      });
    } catch (error) {
      console.error("Error fetching course sub-details:", error);
      Swal.fire("Error!", "Failed to load course sub-details.", "error");
    }
  };

  const fetchAssignments = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}assignment/AssignmentMasterList`, {
        headers: getHeaders(),
      });
      
      // Ensure assignments have courseSubDetailId property
      const assignments = Array.isArray(response.data) ? response.data : [];
      const assignmentsWithSubDetails = assignments.map(assignment => ({
        ...assignment,
        courseSubDetailId: assignment.courseSubDetailId || 0
      }));
      
      updateState({
        assignments: assignmentsWithSubDetails,
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching assignments:", error);
      Swal.fire("Error!", "Failed to load assignments.", "error");
      updateState({ loading: false });
    }
  };

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token"),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value,
      },
    });
  };

  const handleCourseSelectChange = (selectedOption) => {
    const newFormData = {
      ...state.formData,
      courseId: selectedOption ? selectedOption.value : 0,
      courseName: selectedOption ? selectedOption.label : "",
      courseSubDetailId: 0, // Reset sub-detail when course changes
    };
    updateState({ formData: newFormData });
  };

  const handleSubDetailSelectChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        courseSubDetailId: selectedOption ? selectedOption.value : 0,
      },
    });
  };

  const getFilteredSubDetails = () => {
    if (!state.formData.courseId) return [];
    return state.courseSubDetails
      .filter((detail) => detail.courseId === state.formData.courseId)
      .map((detail) => ({
        value: detail.courseSubdetailsId,
        label: detail.courseTopicName,
      }));
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        updateState({ searchQuery: query, currentPage: 0 });
      }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0,
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        assignmentId: 0,
        courseId: 0,
        courseSubDetailId: 0,
        courseName: "",
        assignmentTittle: "",
        assignmentDescription: "",
        assignmentDueDate: "",
        assignmentTotalPoints: 0,
        assignmentPassingMarks: 0,
        assignmentDetailStatus: true,
      },
    });
  };

  const handleEdit = (assignment) => {
    let formattedDate = '';
    if (assignment.assignmentDueDate) {
      const date = new Date(assignment.assignmentDueDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }
    
    updateState({
      formData: {
        ...assignment,
        courseSubDetailId: assignment.courseSubDetailId || 0, // Ensure this is set
        assignmentDetailStatus: assignment.assignmentDetailStatus !== false,
        assignmentDueDate: formattedDate,
      },
    });
  };

  const handleDelete = async (assignmentId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}assignment/DeleteAssignmentDetails`,
        { assignmentId },
        { headers: getHeaders() }
      );
      updateState({
        assignments: state.assignments.filter(
          (item) => item.assignmentId !== assignmentId
        ),
      });
      Swal.fire("Deleted!", "Assignment has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting assignment:", error);
      Swal.fire("Error", "Failed to delete assignment", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    // Validate form
    if (
      !state.formData.courseId ||
      !state.formData.courseSubDetailId ||
      !state.formData.assignmentTittle ||
      !state.formData.assignmentDueDate ||
      !state.formData.assignmentTotalPoints ||
      !state.formData.assignmentPassingMarks
    ) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}assignment/AssignmentAddEdit`,
        {
          ...state.formData,
          assignmentDetailStatus: state.formData.assignmentDetailStatus ? 1 : 0,
        },
        { headers: getHeaders() }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text:
          response.data.response === "SUCCESS"
            ? "Assignment saved successfully"
            : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchAssignments();
        document.getElementById("closeAssignmentModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving assignment:", error);
      Swal.fire("Error", "Failed to save assignment", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  const filteredAssignments = state.assignments.filter((item) => {
    const matchesSearch =
      (item.assignmentTittle || '').toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      (item.assignmentDescription || '').toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      (item.courseName || '').toLowerCase().includes(state.searchQuery.toLowerCase());

    const matchesStatus =
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.assignmentDetailStatus) ||
      (state.filterStatus === "inactive" && !item.assignmentDetailStatus);

    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredAssignments.length / state.itemsPerPage);
  const displayedAssignments = filteredAssignments.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  const getCourseName = (courseId) => {
    const course = state.mcourses.find((c) => c.courseId === courseId);
    return course ? course.courseName : "N/A";
  };

  const getSubDetailName = (subDetailId) => {
    if (!subDetailId) return "N/A";
    const subDetail = state.courseSubDetails.find(
      (d) => d.courseSubdetailsId === subDetailId
    );
    return subDetail ? subDetail.courseTopicName : "N/A";
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">
                      Assignment Management
                    </h4>

                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Title"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>

                      {/* Filter */}
                      {/* <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: "120px" }}
                        >
                          <option value="all">All Status</option>
                          <option value="inactive">Active</option>
                          <option value="active">Inactive</option>
                        </select>
                      </div> */}

                      {/* Add Assignment Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#assignmentModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">
                          Add Assignment
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Assignment Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "200px" }}
                      >
                        <ClipLoader color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading assignments...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped assignment-master-table">
                          <thead className="table-primary">
                            <tr>
                              <th className="col-assignment-id">ID</th>
                              <th className="col-assignment-course">Course</th>
                              <th className="col-assignment-topic">Course Topic</th>
                              <th className="col-assignment-title">Title</th>
                              <th className="col-assignment-date">Due Date</th>
                              <th className="col-assignment-points">Points</th>
                              <th className="col-assignment-actions text-end">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedAssignments.length > 0 ? (
                              displayedAssignments.map((item) => (
                                <tr key={item.assignmentId}>
                                  <td className="col-assignment-id">{item.assignmentId}</td>
                                  <td className="col-assignment-course">{getCourseName(item.courseId)}</td>
                                  <td className="col-assignment-topic">
                                    <div className="text-truncate-assignment" title={getSubDetailName(item.courseSubDetailId)}>
                                      {getSubDetailName(item.courseSubDetailId)}
                                    </div>
                                  </td>
                                  <td className="col-assignment-title">
                                    <div className="text-truncate-assignment" title={item.assignmentTittle}>
                                      {item.assignmentTittle}
                                    </div>
                                  </td>
                                  <td className="col-assignment-date">
                                    {new Date(
                                      item.assignmentDueDate
                                    ).toLocaleDateString()}
                                  </td>
                                  <td className="col-assignment-points">{item.assignmentTotalPoints}</td>
                                  <td className="col-assignment-actions text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#assignmentModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          handleDelete(item.assignmentId)
                                        }
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No assignments"
                                      style={{ opacity: 0.5, width: "60px" }}
                                    />
                                    <p className="mt-3 text-muted">
                                      No assignments found
                                    </p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#assignmentModal"
                                      onClick={resetForm}
                                    >
                                      Add New Assignment
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedAssignments.length > 0 && (
                          <ReactPaginate
                            previousLabel={
                              <i className="fas fa-chevron-left"></i>
                            }
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={
                              "pagination justify-content-center mt-4"
                            }
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      <div
        className="modal fade"
        id="assignmentModal"
        tabIndex="-1"
        aria-labelledby="assignmentModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="assignmentModalLabel">
                {state.formData.assignmentId > 0
                  ? "Edit Assignment"
                  : "Add New Assignment"}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row">
                  <div className="col-md-6 mb-3 pe-0">
                    <label htmlFor="courseId" className="form-label required">
                      Course
                    </label>
                    <Select
                      id="courseId"
                      options={state.mcourses.map((course) => ({
                        value: course.courseId,
                        label: course.courseName,
                      }))}
                      value={
                        state.mcourses.find(
                          (course) =>
                            course.courseId === state.formData.courseId
                        )
                          ? {
                              value: state.formData.courseId,
                              label: state.mcourses.find(
                                (course) =>
                                  course.courseId === state.formData.courseId
                              ).courseName,
                            }
                          : null
                      }
                      onChange={handleCourseSelectChange}
                      placeholder="Select Course"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      required
                    />
                  </div>
                  <div className="col-md-5 mb-3 flex-grow-1 ps-0">
                    <label
                      htmlFor="courseSubDetailId"
                      className="form-label required"
                    >
                      Course Topic
                    </label>
                    <Select
                      id="courseSubDetailId"
                      options={getFilteredSubDetails()}
                      value={
                        getFilteredSubDetails().find(
                          (option) =>
                            option.value === state.formData.courseSubDetailId
                        ) || null
                      }
                      onChange={handleSubDetailSelectChange}
                      placeholder={
                        state.formData.courseId 
                          ? "Select Course Topic" 
                          : "Please select a course first"
                      }
                      isDisabled={!state.formData.courseId}
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3 pe-0">
                    <label
                      htmlFor="assignmentTittle"
                      className="form-label required"
                    >
                      Title
                    </label>
                    <input
                      type="text"
                      className="form-control p-3"
                      id="assignmentTittle"
                      name="assignmentTittle"
                      value={state.formData.assignmentTittle}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-5 mb-3 ps-0 flex-grow-1">
                    <label
                      htmlFor="assignmentDueDate"
                      className="form-label required"
                    >
                      Due Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="assignmentDueDate"
                      name="assignmentDueDate"
                      value={state.formData.assignmentDueDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="mb-3">
                    <label htmlFor="assignmentDescription" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="assignmentDescription"
                      name="assignmentDescription"
                      rows="2"
                      value={state.formData.assignmentDescription}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3 pe-0">
                    <label
                      htmlFor="assignmentTotalPoints"
                      className="form-label required"
                    >
                      Total Points
                    </label>
                    <input
                      type="number"
                      className="form-control p-2"
                      id="assignmentTotalPoints"
                      name="assignmentTotalPoints"
                      value={state.formData.assignmentTotalPoints}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                  <div className="col-md-5 mb-3 ps-0 flex-grow-1">
                    <label
                      htmlFor="assignmentPassingMarks"
                      className="form-label required"
                    >
                      Passing Marks
                    </label>
                    <input
                      type="number"
                      className="form-control p-2"
                      id="assignmentPassingMarks"
                      name="assignmentPassingMarks"
                      value={state.formData.assignmentPassingMarks}
                      onChange={handleInputChange}
                      min="0"
                      max={state.formData.assignmentTotalPoints}
                      required
                    />
                  </div>
                </div>
                {/* {state.formData.assignmentId > 0 && (
                  <div className="mb-3">
                    <label
                      htmlFor="assignmentDetailStatus"
                      className="form-label"
                    >
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="assignmentDetailStatus"
                      name="assignmentDetailStatus"
                      value={state.formData.assignmentDetailStatus}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                )} */}
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                id="closeAssignmentModal"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}